import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { Badge } from 'primereact/badge';
import { useAuth } from '../contexts/AuthContext';
import { 
  CogIcon,
  BuildingOfficeIcon,
  UserIcon,
  ShieldCheckIcon,
  BellIcon,
  DatabaseIcon,
  CloudArrowUpIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  
  // School Settings
  const [schoolSettings, setSchoolSettings] = useState({
    name: 'Demo School',
    address: '123 Education Street',
    phone: '+1-234-567-8900',
    email: 'admin@demoschool.edu',
    website: 'www.demoschool.edu',
    logo: null,
    establishedYear: '2000',
    principalName: 'Dr. John Smith',
    description: 'A premier educational institution committed to excellence.',
    timezone: 'America/New_York',
    currency: 'USD',
    academicYearStart: 'September',
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    allowRegistration: true,
    emailNotifications: true,
    smsNotifications: false,
    backupFrequency: 'daily',
    sessionTimeout: 30,
    maxFileSize: 10,
    allowedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'png'],
    defaultLanguage: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12'
  });

  // User Management
  const [users, setUsers] = useState([
    { id: 1, username: 'admin', email: 'admin@school.com', role: 'admin', status: 'active', lastLogin: '2024-01-15' },
    { id: 2, username: 'teacher1', email: 'teacher1@school.com', role: 'teacher', status: 'active', lastLogin: '2024-01-14' },
    { id: 3, username: 'accountant', email: 'accountant@school.com', role: 'accountant', status: 'active', lastLogin: '2024-01-13' }
  ]);

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    passwordMinLength: 8,
    requireSpecialChars: true,
    requireNumbers: true,
    requireUppercase: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    twoFactorAuth: false,
    ipWhitelist: [],
    auditLogging: true
  });

  // Dialogs
  const [userDialog, setUserDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    role: '',
    password: '',
    confirmPassword: ''
  });

  const timezones = [
    { label: 'Eastern Time (ET)', value: 'America/New_York' },
    { label: 'Central Time (CT)', value: 'America/Chicago' },
    { label: 'Mountain Time (MT)', value: 'America/Denver' },
    { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
    { label: 'UTC', value: 'UTC' }
  ];

  const currencies = [
    { label: 'US Dollar (USD)', value: 'USD' },
    { label: 'Euro (EUR)', value: 'EUR' },
    { label: 'British Pound (GBP)', value: 'GBP' },
    { label: 'Indian Rupee (INR)', value: 'INR' }
  ];

  const roles = [
    { label: 'Administrator', value: 'admin' },
    { label: 'Management', value: 'management' },
    { label: 'Teacher', value: 'teacher' },
    { label: 'Accountant', value: 'accountant' }
  ];

  const backupFrequencies = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
  ];

  const languages = [
    { label: 'English', value: 'en' },
    { label: 'Spanish', value: 'es' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' }
  ];

  const handleSaveSchoolSettings = async () => {
    try {
      setLoading(true);
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'School settings saved successfully'
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save school settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    try {
      setLoading(true);
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'System settings saved successfully'
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save system settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setUserForm({
      username: '',
      email: '',
      role: '',
      password: '',
      confirmPassword: ''
    });
    setSelectedUser(null);
    setUserDialog(true);
  };

  const handleEditUser = (userData) => {
    setUserForm({
      username: userData.username,
      email: userData.email,
      role: userData.role,
      password: '',
      confirmPassword: ''
    });
    setSelectedUser(userData);
    setUserDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      setLoading(true);
      
      if (selectedUser) {
        // Update existing user
        setUsers(prev => prev.map(u => 
          u.id === selectedUser.id 
            ? { ...u, username: userForm.username, email: userForm.email, role: userForm.role }
            : u
        ));
      } else {
        // Create new user
        const newUser = {
          id: users.length + 1,
          username: userForm.username,
          email: userForm.email,
          role: userForm.role,
          status: 'active',
          lastLogin: 'Never'
        };
        setUsers(prev => [...prev, newUser]);
      }
      
      setUserDialog(false);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: selectedUser ? 'User updated successfully' : 'User created successfully'
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save user'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userData) => {
    setUsers(prev => prev.filter(u => u.id !== userData.id));
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'User deleted successfully'
    });
  };

  const handleBackup = async () => {
    try {
      setLoading(true);
      // Simulate backup process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Database backup completed successfully'
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Backup failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Badge 
        value={rowData.status} 
        severity={rowData.status === 'active' ? 'success' : 'danger'}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex space-x-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-warning"
          onClick={() => handleEditUser(rowData)}
          tooltip="Edit User"
        />
        <Button
          icon="pi pi-key"
          className="p-button-rounded p-button-text p-button-info"
          onClick={() => {
            setSelectedUser(rowData);
            setPasswordDialog(true);
          }}
          tooltip="Reset Password"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDeleteUser(rowData)}
          tooltip="Delete User"
          disabled={rowData.username === user?.username}
        />
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <Toast ref={toast} />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600 mt-1">Configure school and system settings</p>
        </div>
      </div>

      <TabView>
        <TabPanel header="School Information" leftIcon="pi pi-building">
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    School Name *
                  </label>
                  <InputText
                    value={schoolSettings.name}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Principal Name
                  </label>
                  <InputText
                    value={schoolSettings.principalName}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, principalName: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <InputText
                    value={schoolSettings.phone}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, phone: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <InputText
                    value={schoolSettings.email}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, email: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <InputText
                    value={schoolSettings.website}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, website: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Established Year
                  </label>
                  <InputText
                    value={schoolSettings.establishedYear}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, establishedYear: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <InputTextarea
                  value={schoolSettings.address}
                  onChange={(e) => setSchoolSettings({ ...schoolSettings, address: e.target.value })}
                  rows={3}
                  className="w-full"
                />
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <InputTextarea
                  value={schoolSettings.description}
                  onChange={(e) => setSchoolSettings({ ...schoolSettings, description: e.target.value })}
                  rows={3}
                  className="w-full"
                />
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold mb-4">Regional Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <Dropdown
                    value={schoolSettings.timezone}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, timezone: e.value })}
                    options={timezones}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <Dropdown
                    value={schoolSettings.currency}
                    onChange={(e) => setSchoolSettings({ ...schoolSettings, currency: e.value })}
                    options={currencies}
                    className="w-full"
                  />
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold mb-4">School Logo</h3>
              <FileUpload
                mode="basic"
                name="logo"
                accept="image/*"
                maxFileSize={1000000}
                chooseLabel="Upload Logo"
                className="w-full"
              />
            </Card>

            <div className="flex justify-end">
              <Button
                label="Save School Settings"
                icon="pi pi-save"
                onClick={handleSaveSchoolSettings}
                loading={loading}
                className="p-button-success"
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel header="System Configuration" leftIcon="pi pi-cog">
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold mb-4">General Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Maintenance Mode</label>
                    <p className="text-sm text-gray-600">Enable to restrict access during maintenance</p>
                  </div>
                  <InputSwitch
                    checked={systemSettings.maintenanceMode}
                    onChange={(e) => setSystemSettings({ ...systemSettings, maintenanceMode: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Allow Registration</label>
                    <p className="text-sm text-gray-600">Allow new users to register</p>
                  </div>
                  <InputSwitch
                    checked={systemSettings.allowRegistration}
                    onChange={(e) => setSystemSettings({ ...systemSettings, allowRegistration: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Email Notifications</label>
                    <p className="text-sm text-gray-600">Send email notifications</p>
                  </div>
                  <InputSwitch
                    checked={systemSettings.emailNotifications}
                    onChange={(e) => setSystemSettings({ ...systemSettings, emailNotifications: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">SMS Notifications</label>
                    <p className="text-sm text-gray-600">Send SMS notifications</p>
                  </div>
                  <InputSwitch
                    checked={systemSettings.smsNotifications}
                    onChange={(e) => setSystemSettings({ ...systemSettings, smsNotifications: e.value })}
                  />
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold mb-4">System Preferences</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Language
                  </label>
                  <Dropdown
                    value={systemSettings.defaultLanguage}
                    onChange={(e) => setSystemSettings({ ...systemSettings, defaultLanguage: e.value })}
                    options={languages}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Backup Frequency
                  </label>
                  <Dropdown
                    value={systemSettings.backupFrequency}
                    onChange={(e) => setSystemSettings({ ...systemSettings, backupFrequency: e.value })}
                    options={backupFrequencies}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <InputText
                    value={systemSettings.sessionTimeout}
                    onChange={(e) => setSystemSettings({ ...systemSettings, sessionTimeout: parseInt(e.target.value) || 30 })}
                    className="w-full"
                    type="number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max File Size (MB)
                  </label>
                  <InputText
                    value={systemSettings.maxFileSize}
                    onChange={(e) => setSystemSettings({ ...systemSettings, maxFileSize: parseInt(e.target.value) || 10 })}
                    className="w-full"
                    type="number"
                  />
                </div>
              </div>
            </Card>

            <div className="flex justify-end">
              <Button
                label="Save System Settings"
                icon="pi pi-save"
                onClick={handleSaveSystemSettings}
                loading={loading}
                className="p-button-success"
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel header="User Management" leftIcon="pi pi-users">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">System Users</h3>
              <Button
                label="Add User"
                icon="pi pi-plus"
                onClick={handleCreateUser}
                className="p-button-success"
              />
            </div>
            
            <DataTable value={users} responsiveLayout="scroll">
              <Column field="username" header="Username" sortable />
              <Column field="email" header="Email" sortable />
              <Column field="role" header="Role" />
              <Column field="status" header="Status" body={statusBodyTemplate} />
              <Column field="lastLogin" header="Last Login" />
              <Column body={actionBodyTemplate} header="Actions" />
            </DataTable>
          </Card>
        </TabPanel>

        <TabPanel header="Security" leftIcon="pi pi-shield">
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold mb-4">Password Policy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Password Length
                  </label>
                  <InputText
                    value={securitySettings.passwordMinLength}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, passwordMinLength: parseInt(e.target.value) || 8 })}
                    className="w-full"
                    type="number"
                    min="6"
                    max="20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Login Attempts
                  </label>
                  <InputText
                    value={securitySettings.maxLoginAttempts}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, maxLoginAttempts: parseInt(e.target.value) || 5 })}
                    className="w-full"
                    type="number"
                    min="3"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Require Special Characters</label>
                    <p className="text-sm text-gray-600">Password must contain special characters</p>
                  </div>
                  <InputSwitch
                    checked={securitySettings.requireSpecialChars}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, requireSpecialChars: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Require Numbers</label>
                    <p className="text-sm text-gray-600">Password must contain numbers</p>
                  </div>
                  <InputSwitch
                    checked={securitySettings.requireNumbers}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, requireNumbers: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Require Uppercase Letters</label>
                    <p className="text-sm text-gray-600">Password must contain uppercase letters</p>
                  </div>
                  <InputSwitch
                    checked={securitySettings.requireUppercase}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, requireUppercase: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Two-Factor Authentication</label>
                    <p className="text-sm text-gray-600">Enable 2FA for all users</p>
                  </div>
                  <InputSwitch
                    checked={securitySettings.twoFactorAuth}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, twoFactorAuth: e.value })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium">Audit Logging</label>
                    <p className="text-sm text-gray-600">Log all user activities</p>
                  </div>
                  <InputSwitch
                    checked={securitySettings.auditLogging}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, auditLogging: e.value })}
                  />
                </div>
              </div>
            </Card>
          </div>
        </TabPanel>

        <TabPanel header="Backup & Maintenance" leftIcon="pi pi-database">
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold mb-4">Database Backup</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Manual Backup</h4>
                    <p className="text-sm text-gray-600">Create an immediate backup of the database</p>
                  </div>
                  <Button
                    label="Create Backup"
                    icon="pi pi-download"
                    onClick={handleBackup}
                    loading={loading}
                    className="p-button-info"
                  />
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900">Automatic Backups</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Automatic backups are scheduled {systemSettings.backupFrequency}
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Last backup: January 15, 2024 at 2:00 AM
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold mb-4">System Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Version:</span>
                    <span className="font-medium">1.0.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Database:</span>
                    <span className="font-medium">MySQL 8.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Server:</span>
                    <span className="font-medium">Node.js 18.x</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Uptime:</span>
                    <span className="font-medium">15 days, 4 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Storage Used:</span>
                    <span className="font-medium">2.4 GB / 10 GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Users:</span>
                    <span className="font-medium">24</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabPanel>
      </TabView>

      {/* User Dialog */}
      <Dialog
        header={selectedUser ? 'Edit User' : 'Create New User'}
        visible={userDialog}
        onHide={() => setUserDialog(false)}
        style={{ width: '500px' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setUserDialog(false)}
              className="p-button-text"
            />
            <Button
              label={selectedUser ? 'Update' : 'Create'}
              icon="pi pi-check"
              onClick={handleSaveUser}
              loading={loading}
              className="p-button-success"
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username *
            </label>
            <InputText
              value={userForm.username}
              onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <InputText
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              className="w-full"
              type="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role *
            </label>
            <Dropdown
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.value })}
              options={roles}
              className="w-full"
            />
          </div>
          {!selectedUser && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <Password
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="w-full"
                  toggleMask
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <Password
                  value={userForm.confirmPassword}
                  onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                  className="w-full"
                  toggleMask
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog
        header="Reset Password"
        visible={passwordDialog}
        onHide={() => setPasswordDialog(false)}
        style={{ width: '400px' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setPasswordDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Reset Password"
              icon="pi pi-key"
              onClick={() => {
                setPasswordDialog(false);
                toast.current?.show({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Password reset email sent'
                });
              }}
              className="p-button-warning"
            />
          </div>
        }
      >
        <div className="text-center py-4">
          <KeyIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p>
            Are you sure you want to reset the password for{' '}
            <strong>{selectedUser?.username}</strong>?
          </p>
          <p className="text-sm text-gray-600 mt-2">
            A password reset email will be sent to their registered email address.
          </p>
        </div>
      </Dialog>
    </div>
  );
};

export default Settings;